import { getHotelOffers, getFlightOffers } from "@/lib/api/client"
import type {
  HotelOffersResponse,
  FlightOffersResponse,
} from "@/lib/api/types"
import { lookupCityCode } from "@/lib/airports"
import type { FlightOptionView, PackageType } from "@/types/booking"

interface SearchHotelOffersInput {
  eventId: string
  stayStartDate: string
  stayEndDate: string
  passengerCount: number
  packageType: PackageType
  shared?: boolean
}

export type { HotelOffersResponse, FlightOffersResponse }

export interface SearchHotelOffersResult {
  offers: HotelOffersResponse
}

interface SearchFlightOffersInput {
  eventId: string
  stayStartDate: string
  stayEndDate: string
  passengerCount: number
  departureAirports: string[]
  destination?: string
  mode?: "hotel_flight" | "flight"
}

export interface SearchFlightOffersResult {
  flightOffers: FlightOffersResponse
  flightOptions: FlightOptionView[]
}

function buildFlightOptions(
  flightOffers: FlightOffersResponse
): FlightOptionView[] {
  if (flightOffers.flightOptions.length === 0) return []

  return flightOffers.flightOptions.map((flight) => {
    const airportCode = flight.outbound.originAirportCode
    const found = lookupCityCode(airportCode)
    const departureAirport = found ?? {
      code: airportCode,
      city: airportCode,
      country: "",
    }

    return {
      ...flight,
      selectionId: `${airportCode}:${flight.id}`,
      departureAirport,
    }
  })
}

export async function searchHotelOffers({
  eventId,
  stayStartDate,
  stayEndDate,
  passengerCount,
  shared,
}: SearchHotelOffersInput): Promise<SearchHotelOffersResult> {
  const offers = await getHotelOffers({
    eventId,
    stayStartDate,
    stayEndDate,
    passengerCount,
    shared,
  })

  return { offers }
}

export async function searchFlightOffers({
  eventId,
  stayStartDate,
  stayEndDate,
  passengerCount,
  departureAirports,
  destination,
  mode,
}: SearchFlightOffersInput): Promise<SearchFlightOffersResult> {
  const flightOffers = await getFlightOffers({
    eventId,
    stayStartDate,
    stayEndDate,
    passengerCount,
    departureAirports,
    destination,
    mode,
  })

  return {
    flightOffers,
    flightOptions: buildFlightOptions(flightOffers),
  }
}
